import { useQueryClient } from '@tanstack/react-query';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import type React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import logo from '@/assets/logo.svg';
import { BoxedIconMessage } from '@/components/BoxedIconMessage';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import PasswordInput from '@/components/PasswordInput/PasswordInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { isLocalStorageAvailable } from '@/utils';

import {
	StyledBG,
	StyledCopy,
	StyledFeatherBackgroundBottom,
	StyledFeatherBackgroundTop,
	StyledForm,
	StyledFormFooter,
} from './SignInStyles';

// Initialize Firebase (only for password validation during migration)
if (!getApps()?.length) {
	initializeApp({
		apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
		authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
		databaseURL: import.meta.env.VITE_FIREBASE_DB_URL,
		projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
		storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
		messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	});
} else {
	getApp();
}

interface FormValues {
	email: string;
	password: string;
}

const SignIn = () => {
	const {
		register,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm<FormValues>();
	const currentYear = new Date().getFullYear();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [messageState, setMessageState] = useState({
		message: '',
		messageColor: 'var(--red-200)',
		hasError: false,
	});

	const location = useLocation();
	const getPath = () => {
		const locationStateFrom = (location.state as Record<string, string>)?.from;
		return locationStateFrom || '/dashboard/courses';
	};

	const getLocalPreferences = () => {
		const preferences = localStorage.getItem('elevatePreferences') || '{}';

		return JSON.parse(preferences);
	};

	const isEmailSaved = (): boolean => {
		if (isLocalStorageAvailable()) {
			const isSaved = getLocalPreferences()?.isSaved || false;
			return isSaved;
		}
		return false;
	};

	const onSubmit: SubmitHandler<FormValues> = async (signInFormData) => {
		const { email, password } = signInFormData;
		const preferences = getLocalPreferences();
		const trimmedEmail = email.trim();

		if (isEmailSaved()) {
			const updatedPreferences = { ...preferences, email: trimmedEmail };
			localStorage.setItem('elevatePreferences', JSON.stringify(updatedPreferences));
		}

		try {
			// Step 1: Try Supabase login first (for users who have already migrated)
			const { data: supabaseData, error: supabaseError } = await supabase.auth.signInWithPassword({
				email: trimmedEmail,
				password,
			});

			if (supabaseData?.user) {
				// Supabase login successful - wait for auth state to propagate
				// Invalidate queries to refetch with new auth state
				await queryClient.invalidateQueries();

				// Wait a bit for auth state change to propagate
				await new Promise<void>((resolve) => {
					setTimeout(() => {
						resolve();
					}, 100);
				});

				// Verify session is set
				const {
					data: { session },
				} = await supabase.auth.getSession();
				if (session?.user) {
					setMessageState({ ...messageState, message: '' });
					const path = getPath();
					navigate(path);
					return;
				}
			}

			// Step 2: If Supabase fails, try Firebase (for users who haven't migrated yet)
			if (supabaseError) {
				const auth = getAuth();
				const firebaseResult = await signInWithEmailAndPassword(auth, trimmedEmail, password);

				if (firebaseResult.user) {
					// Firebase login successful - now migrate password to Supabase via Edge Function
					try {
						const response = await fetch(
							`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/migrate-password`,
							{
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
									Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
								},
								body: JSON.stringify({
									email: trimmedEmail,
									password,
									firebase_uid: firebaseResult.user.uid,
									school_id: import.meta.env.VITE_SCHOOL_ID,
								}),
							}
						);

						const result = await response.json();

						if (result.success) {
							// Password migrated! Now sign in with Supabase
							const { data: signInData } = await supabase.auth.signInWithPassword({
								email: trimmedEmail,
								password,
							});

							if (signInData?.user) {
								// Invalidate queries to refetch with new auth state
								await queryClient.invalidateQueries();

								// Wait for auth state to propagate
								await new Promise<void>((resolve) => {
									setTimeout(() => {
										resolve();
									}, 100);
								});

								// Verify session is set
								const {
									data: { session },
								} = await supabase.auth.getSession();
								if (session?.user) {
									setMessageState({ ...messageState, message: '' });
									navigate(getPath());
									return;
								}
							}
						}
					} catch (migrationError) {
						// Migration failed but Firebase login worked, continue anyway
						// eslint-disable-next-line no-console
						console.warn('Password migration failed:', migrationError);
					}

					setMessageState({ ...messageState, message: '' });
					navigate(getPath());
					return;
				}
			}

			// If we get here, both auth methods failed
			throw new Error('Invalid credentials');
		} catch (error) {
			const errorCode = (error as Record<string, string>).code;
			let errorMsg = 'Something went wrong. Please try again.';

			if (
				errorCode === 'auth/wrong-password' ||
				errorCode === 'auth/user-not-found' ||
				errorCode === 'auth/invalid-credential'
			) {
				errorMsg = 'Wrong email or password.';
			}
			if (errorCode === 'auth/network-request-failed') {
				errorMsg = 'Your network connection may be down. Please try again.';
			}
			setMessageState({
				message: errorMsg,
				messageColor: 'var(--red-200)',
				hasError: true,
			});
		}
	};

	const setInitialEmail = () => {
		if (isLocalStorageAvailable()) {
			const email = getLocalPreferences()?.email || '';
			return email;
		}

		return '';
	};

	const saveEmailAddress = (isChecked: boolean) => {
		if (isChecked) {
			const preferences = {
				email: '',
				isSaved: isChecked,
			};
			localStorage.setItem('elevatePreferences', JSON.stringify(preferences));
		} else {
			localStorage.removeItem('elevatePreferences');
		}
	};

	const navigateToResetPassword = (e: React.MouseEvent) => {
		e.preventDefault();
		navigate('/reset-password');
	};

	return (
		<StyledBG>
			<StyledFeatherBackgroundTop />
			<StyledFeatherBackgroundBottom />
			<Link to="/" className="logo" aria-label="Home">
				<img src={`${logo}`} alt="Elevate logo" />
			</Link>
			<StyledForm onSubmit={handleSubmit(onSubmit)}>
				<label htmlFor="email">Email Address</label>
				<Input
					type="text"
					id="email"
					{...register('email', {
						required: 'Email is required',
						pattern: {
							value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
							message: 'Invalid email address',
						},
					})}
					defaultValue={setInitialEmail()}
				/>
				<FormErrorMessage
					className="error-message"
					isShowing={!!errors?.email}
					message={errors.email?.message}
				/>
				<PasswordInput
					labelText="Password"
					registerOptions={register('password', {
						required: 'Password is required',
						minLength: {
							value: 8,
							message: 'Email must be at least 8 characters',
						},
					})}
					errors={errors}
				/>
				{/* only show button if local storage permissions are set */}
				{isLocalStorageAvailable() && (
					<div className="flex items-center gap-3 mb-4">
						<Checkbox
							id="remember-user"
							defaultChecked={isEmailSaved()}
							onCheckedChange={saveEmailAddress}
						/>
						<label htmlFor="remember-user">Remember Email Address</label>
					</div>
				)}
				<StyledFormFooter>
					<Button
						type="submit"
						onClick={handleSubmit(onSubmit)}
						isLoading={isSubmitting}
						className="w-[max-content]"
						disabled={isSubmitting}
					>
						{isSubmitting ? 'Signing In' : 'Sign In'}
					</Button>
					<a href="/reset-password" onClick={navigateToResetPassword}>
						Forgot Password?
					</a>
				</StyledFormFooter>
				{messageState.message !== '' && (
					<BoxedIconMessage
						message={messageState.message}
						messageColor={messageState.messageColor}
						hasError={messageState.hasError}
					/>
				)}
			</StyledForm>
			<StyledCopy>Copyright All Rights Reserved &#169; {currentYear} | Elevate</StyledCopy>
		</StyledBG>
	);
};

export default SignIn;
